import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router'

import { asyncError, createOrder, saveOrder } from 'redux/actions'
import * as routes from 'settings/_routesSettings'

interface Payload {
  person_profile_product_appointment_id: number
  plan_code: string
  product_group_code: string
  profile_role: string
  username: string
  schedules: Array<{
    start_date: string
    end_date: string
    is_recurring?: boolean
    guests: Array<{
      person_profile_id: number
    }>
  }>
  scheduledFrom: string
  autoReservationEnabled?: boolean
}

interface ICreateOrder {
  isCreateOrderLoading: boolean
  onCreateOrder: (payload: Payload) => Promise<boolean>
}

type AuthState = {
  auth: { 
    isAuth: boolean 
  }
}

export function useCreateOrder(): ICreateOrder {
  const [isCreateOrderLoading, setIsCreateOrderLoading] = useState(false)
  const dispatch = useDispatch()
  const routeMatch = useRouteMatch()
  const history = useHistory()
  const isAuth = useSelector((state: AuthState) => state.auth.isAuth)
  
  async function onCreateOrder(payload: Payload): Promise<boolean> {
    const selectedSchedule = payload?.schedules[0]?.start_date
    if (!isAuth) {
      const redirectUrl = encodeURI(
        `${routeMatch.url}?availabilities=${JSON.stringify(selectedSchedule)}`
      )
      history.push(`${routes.LOGIN}?redirectTo=${redirectUrl}`)
      return false
    }

    try {
      setIsCreateOrderLoading(true)
      dispatch(saveOrder(payload))
      const schedulesAllowRecurringReservationFlow =
        payload.schedules?.every((scheduleItem) => scheduleItem.is_recurring !== false) ?? true
      const shouldDeferPlanCheckoutRedirect =
        Boolean(payload.autoReservationEnabled) &&
        payload.profile_role === 'PSIC' &&
        schedulesAllowRecurringReservationFlow
      const result: unknown = await (dispatch as (a: unknown) => Promise<unknown>)(
        createOrder(payload, !shouldDeferPlanCheckoutRedirect)
      )
      const success = result === true
      return success
    } catch (error) {
      dispatch(asyncError(error))
      return false
    } finally {
      setIsCreateOrderLoading(false)
    }
  }
  
  return {
    isCreateOrderLoading,
    onCreateOrder
  }
}